// Third party libraries
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Divider, Icon, Modal, Form, Checkbox, Message, Loader, Popup } from 'semantic-ui-react';

// Models
import SecurityModel, { ProAffiliationLink } from '../../../../models/security';

// Components
import CircleProgress from './circleProgress';

// Styled components
import OButton from '../../../styled/button';
import { FlexBox } from '../../../styled/wrappers';

// Locales
import I18n from '../../../../i18n';

// Styles
import './inviteStudent.scss';

type Props = {
    key?: string;
    open: boolean;
    currentStudents: number;
    totalStudents: number;
    licenseType: string;
    userSession: {
        _id: string;
        name: string;
        surname: string;
        publicProfile: boolean;
    };
    onClose: () => void
};

const InviteStudent = ({ open, onClose, currentStudents, totalStudents, licenseType, userSession }: Props) => {

    const navigate = useNavigate();
    const params = useParams();

    // Estados para la carga del Link de Afiliación
    const [currentLinkData, setCurrentLinkData] = useState<ProAffiliationLink | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);

    const publicProfile = userSession.publicProfile;

    // Estados para el formulario de creación de un nuevo link
    const [showForm, setShowForm] = useState<boolean>(false);
    const [newMaxStudents, setNewMaxStudents] = useState<number>(1);
    const [newUnlimitedStudents, setNewUnlimitedStudents] = useState<boolean>(false);

    // Que pueda crear links de pago depende de si tiene un perfil publico
    const [newPaymentRequired, setNewPaymentRequired] = useState<boolean>(publicProfile);
    const [newLinkName, setNewLinkName] = useState<string>('');

    const userFullName = userSession.name + ' ' + userSession.surname;

    // Cargar el último link al abrir el modal
    useEffect(() => {
        if (open && userSession._id) {
            fetchLastLink();
        }
    }, [open, userSession._id]);

    // Efecto de seguridad: Si desmarca "Requiere pago", desactivamos e invalidamos el estado "Ilimitado"
    useEffect(() => {
        if (!newPaymentRequired) {
            setNewUnlimitedStudents(false);
        }
    }, [newPaymentRequired]);

    const fetchLastLink = async () => {
        setLoading(true);
        setError(null);
        try {
            const lastLink = await SecurityModel.getLastAffiliationLinkByUserId();
            if (lastLink) setCurrentLinkData(lastLink);
        } catch (err: any) {
            console.error("Error obteniendo el último link:", err);
            setError(I18n.t('students.inviteModal.fetchingFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            // Si es ilimitado guardamos 0 según lo requerido por el servidor, si no, el número ingresado
            const bodyData = {
                maxStudents: newUnlimitedStudents ? 0 : Number(newMaxStudents),
                name: newLinkName,
                paymentRequired: newPaymentRequired || false
            };

            const response = await SecurityModel.createProAffiliationLink(bodyData);

            if (response && !response.error) {
                setCurrentLinkData(response);
                setShowForm(false); // Ocultar formulario tras creación exitosa
                setLinkCopied(false);
                // Resetear campos
                setNewLinkName('');
                setNewMaxStudents(1);
                setNewUnlimitedStudents(false);
                setNewPaymentRequired(publicProfile);
            } else {
                throw new Error(response.error || 'Failed to create');
            }
        } catch (err: any) {
            console.error("Error creando el link:", err);
            setError(I18n.t('students.inviteModal.creatingFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    // Construcción dinámica de la URL
    let shareLink = '';
    if (currentLinkData && currentLinkData.code) {
        shareLink = `https://yogabot.app/proLink?code=${currentLinkData.code}`;
        if (params.groupId) {
            shareLink += `&groupId=${params.groupId}`;
        }
    }

    const onWhatsappShare = () => {
        if (!shareLink) return;
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent('' + I18n.t('students.inviteModal.invitationTexts.whatsapp', { userFullName })) + encodeURIComponent(shareLink)}`);
        onClose();
    };

    const onEmailShare = () => {
        if (!shareLink) return;
        window.open(`mailto:?subject=${encodeURIComponent('' + I18n.t('students.inviteModal.invitationTexts.email.subject', { userFullName }))}&body=${encodeURIComponent('' + I18n.t('students.inviteModal.invitationTexts.email.text', { userFullName })) + shareLink}`);
        onClose();
    };

    const onCopyToClipboard = () => {
        if (!shareLink) return;
        navigator.clipboard.writeText(shareLink);
        setLinkCopied(true);
    };

    const onLevelUpAccount = () => {
        onClose();
        navigate('/setup/checkout/plans');
    };

    return (
        <Modal open={open} size='mini' closeIcon onClose={onClose}>
            <Modal.Header>{I18n.t('students.invite')}</Modal.Header>
            <Modal.Content scrolling>
                <Modal.Description>
                    <div style={{ textAlign: 'center' }}>
                        <p><b>{licenseType}</b></p>
                        <p>{I18n.t('students.inviteModal.studentLimitText', { studentLimit: totalStudents })}</p>
                        <p>{I18n.t('students.inviteModal.needMore')}</p>

                        <OButton compact type='button' $color='var(--brand-secondary)' $primary disabled={totalStudents === 100} onClick={onLevelUpAccount}>
                            <p style={{ marginTop: '10px' }}><span>{I18n.t('students.inviteModal.levelUpYourAccount')}</span></p>
                        </OButton>

                        <Divider />

                        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                            <CircleProgress total={totalStudents} current={currentStudents} text={I18n.t('students.inviteModal.students')} large />
                        </div>

                        {error && <Message negative size="tiny">{error}</Message>}

                        {loading ? (
                            <div style={{ padding: '20px' }}><Loader active inline='centered' size='small' /></div>
                        ) : currentLinkData ? (
                            <div style={{ backgroundColor: '#F9F9F9', padding: '12px', borderRadius: '6px', textAlign: 'left', marginBottom: '15px', border: '1px solid #EAEAEA' }}>
                                <p style={{ margin: '0 0 5px 0', fontSize: '0.9em' }}>
                                    <Icon name="linkify" /> <b>{I18n.t('students.inviteModal.generator.activeLink')}</b> {currentLinkData.name || 'Sin nombre'}
                                </p>
                                <p style={{ margin: '0 0 5px 0', fontSize: '0.85em' }}>
                                    <Icon name="users" /> {I18n.t('students.inviteModal.generator.limit')}{' '}
                                    <b>
                                        {currentLinkData.maxStudents === 0
                                            ? I18n.t('students.inviteModal.generator.unlimited', { defaultValue: 'Ilimitado' })
                                            : currentLinkData.maxStudents}
                                    </b>{' '}
                                    estudiantes.
                                </p>
                                <p style={{ margin: '0', fontSize: '0.85em' }}>
                                    <Icon name={currentLinkData.paymentRequired ? "credit card" : "unlock"} />
                                    {' '}{I18n.t('students.inviteModal.generator.requiresPayment')} <b>{currentLinkData.paymentRequired ? 'Sí' : 'No (Gratuito)'}</b>
                                </p>
                            </div>
                        ) : (
                            <p style={{ color: '#777', fontSize: '0.9em' }}>{I18n.t('students.inviteModal.generator.noActiveLink')}</p>
                        )}

                        {shareLink && !showForm && (
                            <>
                                <p><b>{I18n.t('students.inviteModal.invitationLink')}</b></p>
                                <div style={{ backgroundColor: '#F4F4F4', lineHeight: '30px', padding: '5px', wordBreak: 'break-all' }}>
                                    <span className='caption regular'>{linkCopied ? I18n.t('students.inviteModal.linkCopied') : shareLink}</span>
                                </div>
                            </>
                        )}
                    </div>

                    {shareLink && !showForm && (
                        <FlexBox $justifyContent='space-between' style={{ paddingTop: '20px', paddingBottom: '10px' }}>
                            <OButton icon type='button' $color='var(--brand-secondary)' $quarth style={{ width: '33%' }} onClick={onCopyToClipboard}>
                                <Icon name='copy' size='big' />
                                <p style={{ marginTop: '10px' }}><span>{I18n.t('students.inviteModal.copyLink')}</span></p>
                            </OButton>
                            <OButton icon type='button' $color='var(--brand-secondary)' $quarth style={{ width: '33%' }} onClick={onEmailShare}>
                                <Icon name='mail' size='big' />
                                <p style={{ marginTop: '10px' }}><span>{I18n.t('students.inviteModal.sendByEmail')}</span></p>
                            </OButton>
                            <OButton icon type='button' $color='var(--brand-secondary)' $quarth style={{ width: '33%' }} onClick={onWhatsappShare}>
                                <Icon name='whatsapp square' size='big' />
                                <p style={{ marginTop: '10px' }}><span>{I18n.t('students.inviteModal.shareOnWhatsapp')}</span></p>
                            </OButton>
                        </FlexBox>
                    )}

                    <Divider />

                    {/* Formulario de personalización de link */}
                    {!showForm ? (
                        <OButton fluid type='button' $color='var(--brand-secondary)' $quarth onClick={() => setShowForm(true)}>
                            <Icon name="plus" /> {I18n.t('students.inviteModal.generator.title')}
                        </OButton>
                    ) : (
                        <Form onSubmit={handleCreateLink} style={{ marginTop: '10px', textAlign: 'left' }}>
                            <h4 style={{ margin: '0 0 10px 0', textAlign: 'center' }}>{I18n.t('students.inviteModal.generator.newLink')}</h4>

                            <Form.Field size="tiny">
                                <label>{I18n.t('students.inviteModal.generator.linkName')}</label>
                                <input
                                    type="text"
                                    placeholder={I18n.t('students.inviteModal.generator.linkName')}
                                    value={newLinkName}
                                    onChange={(e) => setNewLinkName(e.target.value)}
                                />
                            </Form.Field>

                            <Form.Group widths='equal' style={{ marginBottom: '5px' }}>
                                <Form.Field size="tiny" disabled={newUnlimitedStudents}>
                                    <label>{I18n.t('students.inviteModal.generator.maxStudents')}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10000"
                                        required={!newUnlimitedStudents}
                                        value={newUnlimitedStudents ? '' : newMaxStudents}
                                        onChange={(e) => setNewMaxStudents(Number(e.target.value))}
                                        placeholder={newUnlimitedStudents ? I18n.t('students.inviteModal.generator.unlimited') : '1-10000'}
                                    />
                                </Form.Field>
                                <Form.Field style={{ paddingTop: '25px' }}>
                                    <Popup disabled={publicProfile} content={I18n.t('students.inviteModal.generator.paymentRequiredTooltip')}
                                        trigger={<Checkbox
                                            label="Requiere pago"
                                            checked={publicProfile ? newPaymentRequired : false}
                                            onChange={(_, data) => publicProfile && setNewPaymentRequired(data.checked || false)}
                                            disabled={!publicProfile}
                                        />}
                                    />
                                </Form.Field>
                            </Form.Group>

                            {/* Fila del Checkbox Ilimitado con Popup explicativo de seguridad si no requiere pago */}
                            <Form.Field style={{ paddingBottom: '10px' }}>
                                <Popup
                                    disabled={newPaymentRequired}
                                    content={I18n.t('students.inviteModal.generator.unlimitedSecurityTooltip')}
                                    trigger={
                                        <div>
                                            <Checkbox
                                                label="Cantidad máxima de estudiantes ilimitada"
                                                checked={newUnlimitedStudents}
                                                onChange={(_, data) => setNewUnlimitedStudents(data.checked || false)}
                                                disabled={!newPaymentRequired}
                                            />
                                        </div>
                                    }
                                />
                            </Form.Field>

                            <FlexBox $justifyContent='space-between' style={{ marginTop: '15px' }}>
                                <OButton compact type='button' style={{ width: '48%' }} onClick={() => setShowForm(false)}>
                                    {I18n.t('buttons.actions.cancel')}
                                </OButton>
                                <OButton compact type='submit' $primary style={{ width: '48%' }} disabled={submitting}>
                                    {submitting ? I18n.t('students.inviteModal.generator.gerating') : I18n.t('students.inviteModal.generator.action')}
                                </OButton>
                            </FlexBox>
                        </Form>
                    )}
                </Modal.Description>
            </Modal.Content>
        </Modal>
    );
};

export default InviteStudent;